import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GitHubIcon from '@mui/icons-material/GitHub';
import {
  AccessProblem,
  forkHint,
  GithubAccess,
  needsFreshToken,
} from './githubOrgAccess';
import { AuthorizeOptions, GithubAccessState } from './useGithubAccess';

export interface GithubAccessStatusProps {
  state: GithubAccessState;
  access: GithubAccess[];
  error?: string;
  problem?: AccessProblem;
  authorizeUrl?: string;
  /** The probe targets, named in the "checking…" and "authorized" copy. */
  targets: string[];
  onRetry: (options?: AuthorizeOptions) => void;
  /** Shown once every target checks out. */
  okMessage?: string;
  /** Label for the button before anything has been probed. */
  connectLabel?: string;
  /**
   * Shown as an error beside the connect button while access is unproven, for
   * a step that cannot proceed without it. Also promotes the button, so the
   * one thing the user must do does not look optional.
   */
  requiredHint?: string;
}

/** "ds24-labs and hulkag" — the organizations behind a list of probe targets. */
export function describeTargets(targets: string[]): string {
  const orgs = [...new Set(targets.map(t => t.split('/')[0]).filter(Boolean))];
  if (orgs.length <= 1) return orgs[0] ?? 'GitHub';
  return `${orgs.slice(0, -1).join(', ')} and ${orgs[orgs.length - 1]}`;
}

/** The organization an SSO authorization URL is for, when it names one. */
function orgOfAuthorizeUrl(url: string | undefined): string | undefined {
  return url ? /\/orgs\/([^/]+)\//.exec(url)?.[1] : undefined;
}

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
  flexWrap: 'wrap',
};

/**
 * Shared GitHub-access banner for the internal-app flows: what was checked,
 * what is wrong, and the one button that fixes it. Access problems are
 * reported here, before a run starts, because the same problems surface deep
 * in a scaffolder run as a bare 404.
 */
export const GithubAccessStatus = (props: GithubAccessStatusProps) => {
  const {
    state,
    access,
    error,
    problem,
    authorizeUrl,
    targets,
    onRetry,
    okMessage,
    connectLabel = 'Authorize with GitHub',
    requiredHint,
  } = props;

  const where = describeTargets(targets);
  const ssoOrg = orgOfAuthorizeUrl(authorizeUrl);
  const forks = access.filter(a => a.canPush === false);
  // Some problems cannot be repaired by re-probing the same token — an
  // unauthorized organization (mint a new one after the single sign-on the
  // button above opens) and a token GitHub has rejected outright. For those
  // the retry has to drop the session first, or it re-presents the credential
  // that just failed and the user is stuck on one error forever.
  const retry = () => onRetry({ fresh: needsFreshToken(problem) });

  if (state === 'checking') {
    return (
      <div style={row}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="textSecondary">
          Checking your GitHub access to {where}…
        </Typography>
      </div>
    );
  }

  if (state === 'ok') {
    return (
      <div>
        <div style={row}>
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" color="textSecondary">
            {okMessage ??
              `GitHub access to ${where} authorized — this will run as you.`}
          </Typography>
        </div>
        {forks.map(a => (
          <FormHelperText key={a.target}>⚠ {forkHint(a.target)}</FormHelperText>
        ))}
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={row}>
        {authorizeUrl && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<GitHubIcon />}
            href={authorizeUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ssoOrg ? `Authorize for ${ssoOrg}` : 'Authorize on GitHub'}
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={authorizeUrl ? undefined : <GitHubIcon />}
          onClick={retry}
        >
          {authorizeUrl ? 'Retry' : 'Retry GitHub authorization'}
        </Button>
        <FormHelperText error style={{ flexBasis: '100%' }}>
          {error}
        </FormHelperText>
      </div>
    );
  }

  return (
    <div style={row}>
      <Button
        variant={requiredHint ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        startIcon={<GitHubIcon />}
        onClick={retry}
      >
        {connectLabel}
      </Button>
      {requiredHint && (
        <FormHelperText error style={{ flexBasis: '100%' }}>
          {requiredHint}
        </FormHelperText>
      )}
    </div>
  );
};
